import { forwardRef } from "react";
import { Search as SearchIcon, X } from "lucide-react";
import clsx from "clsx";

export type SearchProps = {
  value?: string;
  onChange?: (value: string) => void;
  onSubmit?: (value: string) => void;
  placeholder?: string;
  size?: "sm" | "md";
  clearable?: boolean;
  className?: string;
  id?: string;
  disabled?: boolean;
  "aria-label"?: string;
};

const sizes = {
  sm: "h-8 text-xs pl-7 pr-7",
  md: "h-10 text-sm pl-9 pr-9",
};

const iconSizes = { sm: 14, md: 16 };

export const Search = forwardRef<HTMLInputElement, SearchProps>(function Search(
  { value, onChange, onSubmit, placeholder = "Search…", size = "md", clearable = true, className, id, disabled, ...rest },
  ref,
) {
  return (
    <div className={clsx("relative inline-flex items-center", className)}>
      <SearchIcon
        size={iconSizes[size]}
        className="absolute left-2.5 text-[var(--color-text-secondary)] pointer-events-none"
      />
      <input
        ref={ref}
        id={id}
        type="search"
        value={value ?? ""}
        onChange={(e) => onChange?.(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && value) onSubmit?.(value);
        }}
        placeholder={placeholder}
        disabled={disabled}
        className={clsx(
          "w-full rounded-[var(--radius-md)] border bg-[var(--color-surface-base)]",
          "border-[var(--color-border-default)] text-[var(--color-text-primary)]",
          "placeholder:text-[var(--color-text-disabled)]",
          "focus:outline-none focus:border-[var(--color-accent)]",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          sizes[size],
        )}
        aria-label={rest["aria-label"] ?? placeholder}
      />
      {clearable && value && !disabled && (
        <button
          type="button"
          onClick={() => onChange?.("")}
          aria-label="Clear search"
          className="absolute right-2 text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] transition-colors"
        >
          <X size={iconSizes[size]} />
        </button>
      )}
    </div>
  );
});
